/**
 * Hourly planners — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_everyday_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const hourlyPlannersPack: ToilePack = {
    slug: 'hourly-planners',
    category: 'planner',
    name: 'Hourly planners',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'daily', label: 'Daily schedule', subject: 'a daily page with hour lines from "6 AM" to "10 PM" down the left edge and an empty writing space beside each hour'},
        {key: 'weekly', label: 'Weekly grid', subject: 'a weekly page with seven day columns headed "Mon" to "Sun" and hour rows down the left side, every cell left empty'},
        {key: 'todo', label: 'With to-do list', subject: 'a day page with an hour column on the left and a box headed "To Do" with empty checkboxes on the right'},
    ],
    styles: [
        {key: 'minimal', label: 'Minimal', style: 'minimal planner design, thin rules, small sans-serif type', palette: 'Black ink only on white paper.', framing: 'The sheet is shown straight on, centered with a wide page margin on a plain light grey ground.'},
        {key: 'pastel', label: 'Pastel', style: 'soft pastel planner design, rounded header tabs, tinted hour bands', palette: 'Pink, mint and lavender on white paper.', framing: 'The sheet is shown straight on, centered with a wide page margin on a plain light grey ground.'},
        {key: 'kraft', label: 'Kraft', style: 'rustic planner design, hand-lettered headings, pencil-thin rules', palette: 'Dark brown ink on kraft paper.', framing: 'The sheet is shown straight on, centered with a wide page margin on a plain light grey ground.'},
    ],
    directions: ['30 min slots', 'night shift', 'pink theme'],
    directionHint: 'try: 30 min slots',
    seo: {
        title: 'Hourly Planners: Day, Week and To-Do Pages',
        metaDescription: 'Nine printable hourly planner pages: a daily hour schedule, a weekly hour grid and a day page with a to-do list, in three styles.',
        h1: 'Hourly planners: daily schedule, weekly grid, to-do day page',
        tagline: 'Three hour-by-hour layouts, each in minimal, pastel or kraft style, for a printed day or week.',
        intro: 'Busy parents, students and freelancers plan the day hour by hour. These nine pages show a daily schedule with hour lines from 6 AM to 10 PM, a weekly grid with seven day columns and empty hour rows, and a day page with an hour column beside a to-do box with empty checkboxes. Each comes in minimal, soft pastel or kraft style. You can also type a request, such as 30 min slots, to shape the layout.',
        useCases: [
            'a daily schedule on a desk',
            'a weekly plan pinned to the fridge',
            "a freelancer's day with a to-do list",
            'a study timetable for a student',
        ],
        faq: [
            {q: 'What hours does the daily page cover?', a: 'It runs from 6 AM to 10 PM, with an empty writing space beside each hour.'},
            {q: 'Can I get half-hour slots?', a: 'You can ask for 30 min slots as a request; the nine pages show whole hours.'},
            {q: 'What are the three styles?', a: 'Minimal has black ink and thin rules, pastel has pink, mint and lavender tabs and bands, and kraft has dark brown ink on kraft paper with pencil-thin rules.'},
        ],
    },
}
